import React, { useEffect, useState } from 'react';
import { getRequest, postRequest, showMessage } from '../../../config';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';

const S = {
    wrap: { padding: '1.5rem', background: '#f5f6fa', minHeight: 120 },
    card: { background: '#fff', borderRadius: 12, border: '1px solid #e4e8f5', boxShadow: '0 2px 8px rgba(74,103,255,0.06)', overflow: 'hidden' },
    cardHead: { padding: '18px 24px 14px', borderBottom: '1px solid #f0f2ff' },
    title: { fontSize: '1.05rem', fontWeight: 700, color: '#1a1d2e', margin: 0 },
    subtitle: { fontSize: '0.8rem', color: '#7c83a0', marginTop: 4 },
    tableWrap: {
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '70vh',
        width: '100%',
        WebkitOverflowScrolling: 'touch',
    },
    thead: { background: '#f8f9ff' },
    th: { padding: '10px 14px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7c83a0', textAlign: 'left', borderBottom: '2px solid #e8eaf5', whiteSpace: 'nowrap' },
    thCenter: { padding: '10px 14px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7c83a0', textAlign: 'center', borderBottom: '2px solid #e8eaf5', whiteSpace: 'nowrap' },
    modRow: { background: 'linear-gradient(90deg,#f0f3ff 0%,#f8f9ff 100%)', cursor: 'pointer', borderTop: '2px solid #e8eaf5', userSelect: 'none' },
    modTd: { padding: '10px 16px' },
    modAccent: { width: 3, height: 34, background: '#4A67FF', borderRadius: 2, flexShrink: 0 },
    modName: { fontSize: '0.88rem', fontWeight: 700, color: '#1a1d2e' },
    modKey: { fontSize: '0.68rem', fontWeight: 700, color: '#4A67FF', background: '#eef0ff', padding: '2px 8px', borderRadius: 10, flexShrink: 0 },
    modMeta: { fontSize: '0.72rem', color: '#9aa0b8' },
    secRow: { borderBottom: '1px solid #f0f2ff', background: '#fff', transition: 'background .1s' },
    secTd: { padding: '8px 12px 8px 32px' },
    secNo: { fontSize: '0.68rem', fontWeight: 700, color: '#4A67FF', background: '#eef0ff', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0 },
    secName: { fontSize: '0.82rem', color: '#374151' },
    catTd: { textAlign: 'center', padding: '8px 14px' },
    cb: { width: 15, height: 15, accentColor: '#4A67FF', cursor: 'pointer' },
};

const TempAllocationSection = ({ categoryList }) => {
    const [tempModules, setTempModules] = useState([]);
    const [openAccordions, setOpenAccordions] = useState({});
    const [loading, setLoading] = useState(false);

    const fetchTempModules = async () => {
        setLoading(true);
        try {
            const res = await getRequest('/admin/temp-modules-admin', 'not-showMessage');
            if (res?.code === 200) setTempModules(res.body || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTempModules(); }, []);

    const toggleAccordion = (moduleId) =>
        setOpenAccordions(prev => ({ [moduleId]: !prev[moduleId] }));

    const handleAssign = async ({ moduleId, sectionNo, category, valueAssign }) => {
        try {
            const res = await postRequest('/admin/assign/category/temp-section', {
                moduleId,
                sectionNo,
                categoryId: category._id,
                type: valueAssign,
            });
            if (res?.code === 200) {
                setTempModules(prev => prev.map(mod => {
                    if (String(mod._id) !== String(moduleId)) return mod;
                    return {
                        ...mod,
                        sections: mod.sections.map(sec => {
                            if (sec.sectionNo !== sectionNo) return sec;
                            return { ...sec, categoryId: valueAssign ? category._id : null };
                        }),
                    };
                }));
                showMessage(res.message || 'Updated.');
            }
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <div className="df jcc aic p2rem" style={{ color: '#7c83a0' }}>Loading…</div>;

    const SECTION_COL_PX = 260;
    const CATEGORY_COL_PX = 100;
    const tableWidth = Math.max(SECTION_COL_PX + (categoryList?.length ?? 0) * CATEGORY_COL_PX, 480);

    return (
        <div style={S.wrap}>
            <div style={S.card}>
                <div style={S.cardHead}>
                    <p style={S.title}>Allocation Section Management</p>
                    <p style={S.subtitle}>Assign a category to each section within a module. Expand a module to see its sections.</p>
                </div>
                <div style={S.tableWrap}>
                    <table
                        style={{
                            width: tableWidth,
                            minWidth: '100%',
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                        }}
                    >
                        <colgroup>
                            <col style={{ width: SECTION_COL_PX }} />
                            {categoryList.map(cat => <col key={cat._id} style={{ width: CATEGORY_COL_PX }} />)}
                        </colgroup>
                        <thead style={S.thead}>
                            <tr>
                                <th
                                    style={{
                                        ...S.th,
                                        position: 'sticky',
                                        left: 0,
                                        top: 0,
                                        zIndex: 4,
                                        backgroundColor: '#f8f9ff',
                                        boxShadow: '4px 0 10px -2px rgba(26, 29, 46, 0.12)',
                                    }}
                                >
                                    Section
                                </th>
                                {categoryList.map(cat => (
                                    <th
                                        key={cat._id}
                                        style={{
                                            ...S.thCenter,
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 3,
                                            backgroundColor: '#f8f9ff',
                                        }}
                                    >
                                        {cat.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tempModules.filter(mod => mod.sections?.length > 0).map(mod => {
                                const isOpen = !!openAccordions[mod._id];
                                return (
                                    <React.Fragment key={mod._id}>
                                        <tr
                                            style={S.modRow}
                                            onClick={() => toggleAccordion(mod._id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleAccordion(mod._id)}
                                        >
                                            <td
                                                style={{
                                                    ...S.modTd,
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 2,
                                                    background: 'linear-gradient(90deg,#f0f3ff 0%,#f8f9ff 100%)',
                                                    boxShadow: '4px 0 10px -2px rgba(26, 29, 46, 0.08)',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div style={S.modAccent} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={S.modName}>{mod.moduleName}</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                                            {mod.moduleKey && <span style={S.modKey}>{mod.moduleKey}</span>}
                                                            <span style={S.modMeta}>{mod.sections?.length || 0} section{mod.sections?.length !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    </div>
                                                    {isOpen
                                                        ? <KeyboardArrowUp style={{ color: '#4A67FF', fontSize: 20, flexShrink: 0 }} />
                                                        : <KeyboardArrowDown style={{ color: '#4A67FF', fontSize: 20, flexShrink: 0 }} />
                                                    }
                                                </div>
                                            </td>
                                            <td colSpan={categoryList.length} style={{ background: 'inherit' }} aria-hidden />
                                        </tr>

                                        {isOpen && (mod.sections || []).map(sec => (
                                            <tr key={`${mod._id}-${sec.sectionNo}`} style={S.secRow}>
                                                <td
                                                    style={{
                                                        ...S.secTd,
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 2,
                                                        backgroundColor: '#fff',
                                                        boxShadow: '4px 0 10px -2px rgba(26, 29, 46, 0.08)',
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span style={S.secNo}>{sec.sectionNo}</span>
                                                        <span style={S.secName}>{sec.sectionName}</span>
                                                    </div>
                                                </td>
                                                {categoryList.map(cat => (
                                                    <td key={cat._id} style={S.catTd}>
                                                        <input
                                                            type="checkbox"
                                                            style={S.cb}
                                                            checked={String(sec.categoryId) === String(cat._id)}
                                                            onChange={e => handleAssign({
                                                                moduleId: mod._id,
                                                                sectionNo: sec.sectionNo,
                                                                category: cat,
                                                                valueAssign: e.target.checked,
                                                            })}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TempAllocationSection;
